import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'

type DragMode = 'low' | 'high' | 'lowGain' | 'midGain' | 'highGain' | null

const BANDS = 36
const W = 1000
const H = 240

type Props = {
  lowMidFreq: number
  midHighFreq: number
  lowGainDb: number
  midGainDb: number
  highGainDb: number
  onLowMidFreqChange: (value: number) => void
  onMidHighFreqChange: (value: number) => void
  onLowGainChange: (value: number) => void
  onMidGainChange: (value: number) => void
  onHighGainChange: (value: number) => void
  getSpectrum?: (bands: number) => Float32Array
}

const MIN_FREQ = 20
const MAX_FREQ = 20000
const MIN_RATIO = 2 // 1 octave
const MIN_DB = -18
const MAX_DB = 18

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v))
}

/** Returns an hsla colour for a spectrum bar index (0 = red/warm, BANDS-1 = cyan/cool) */
function spectrumColor(i: number, total: number, alpha: number) {
  // hue 0 (red) → 180 (cyan)
  const hue = (i / (total - 1)) * 180
  return `hsla(${hue}, 80%, 55%, ${alpha})`
}

export function MultibandView({
  lowMidFreq,
  midHighFreq,
  lowGainDb,
  midGainDb,
  highGainDb,
  onLowMidFreqChange,
  onMidHighFreqChange,
  onLowGainChange,
  onMidGainChange,
  onHighGainChange,
  getSpectrum,
}: Props) {
  const svgRef = useRef<SVGSVGElement | null>(null)
  const [dragMode, setDragMode] = useState<DragMode>(null)
  const [spectrumBars, setSpectrumBars] = useState<Float32Array | null>(null)

  const bandCenters = useMemo(() => {
    const low = Math.sqrt(MIN_FREQ * lowMidFreq)
    const mid = Math.sqrt(lowMidFreq * midHighFreq)
    const high = Math.sqrt(midHighFreq * MAX_FREQ)
    return { low, mid, high }
  }, [lowMidFreq, midHighFreq])

  const freqToX = useCallback((freq: number, width: number) => {
    const minLog = Math.log10(MIN_FREQ)
    const maxLog = Math.log10(MAX_FREQ)
    const x = (Math.log10(freq) - minLog) / (maxLog - minLog)
    return x * width
  }, [])

  const xToFreq = useCallback((x: number, width: number) => {
    const minLog = Math.log10(MIN_FREQ)
    const maxLog = Math.log10(MAX_FREQ)
    const t = clamp(x / width, 0, 1)
    return Math.pow(10, minLog + t * (maxLog - minLog))
  }, [])

  const gainToY = useCallback((gainDb: number, height: number) => {
    const t = (gainDb - MIN_DB) / (MAX_DB - MIN_DB)
    return height - t * height
  }, [])

  const yToGain = useCallback((y: number, height: number) => {
    const t = clamp(1 - y / height, 0, 1)
    return MIN_DB + t * (MAX_DB - MIN_DB)
  }, [])

  // Animate spectrum
  useEffect(() => {
    if (!getSpectrum) return
    let raf = 0
    const tick = () => {
      setSpectrumBars(getSpectrum(BANDS))
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [getSpectrum])

  const handlePointerDown = (mode: DragMode) => (event: ReactPointerEvent<SVGElement>) => {
    event.preventDefault()
    setDragMode(mode)
    ;(event.currentTarget as Element).setPointerCapture(event.pointerId)
  }

  const handlePointerMove = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (!dragMode || !svgRef.current) return
    const rect = svgRef.current.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top

    if (dragMode === 'low') {
      let freq = xToFreq(x, rect.width)
      const maxLow = midHighFreq / MIN_RATIO
      freq = clamp(freq, MIN_FREQ, maxLow)
      onLowMidFreqChange(freq)
    } else if (dragMode === 'high') {
      let freq = xToFreq(x, rect.width)
      const minHigh = lowMidFreq * MIN_RATIO
      freq = clamp(freq, minHigh, MAX_FREQ)
      onMidHighFreqChange(freq)
    } else if (dragMode === 'lowGain') {
      onLowGainChange(yToGain(y, rect.height))
    } else if (dragMode === 'midGain') {
      onMidGainChange(yToGain(y, rect.height))
    } else if (dragMode === 'highGain') {
      onHighGainChange(yToGain(y, rect.height))
    }
  }

  const handlePointerUp = () => {
    setDragMode(null)
  }

  // Build spectrum bar rects
  const barWidth = W / BANDS
  const spectrumRects = spectrumBars
    ? Array.from({ length: BANDS }, (_, i) => {
        const value = spectrumBars[i] || 0
        const barH = Math.max(0, value * H)
        const x = i * barWidth + 1
        const y = H - barH
        return <rect key={i} x={x} y={y} width={Math.max(1, barWidth - 2)} height={barH} fill={spectrumColor(i, BANDS, 0.3)} />
      })
    : null

  return (
    <div className="multiband-graph">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      >
        <rect x="0" y="0" width={W} height={H} className="multiband-graph__bg" />

        {/* Spectrum bars (behind everything) */}
        {spectrumRects}

        <line x1="0" y1={H / 2} x2={W} y2={H / 2} className="multiband-graph__zero" />

        {/* Crossover lines */}
        <line
          x1={freqToX(lowMidFreq, W)}
          y1="0"
          x2={freqToX(lowMidFreq, W)}
          y2={H}
          className="multiband-graph__crossover"
        />
        <line
          x1={freqToX(midHighFreq, W)}
          y1="0"
          x2={freqToX(midHighFreq, W)}
          y2={H}
          className="multiband-graph__crossover"
        />

        {/* Crossover handles */}
        <circle
          cx={freqToX(lowMidFreq, W)}
          cy="24"
          r="10"
          className="multiband-graph__handle"
          onPointerDown={handlePointerDown('low')}
        />
        <circle
          cx={freqToX(midHighFreq, W)}
          cy="24"
          r="10"
          className="multiband-graph__handle"
          onPointerDown={handlePointerDown('high')}
        />

        {/* Band gain lines */}
        <line
          x1="0"
          x2={freqToX(lowMidFreq, W)}
          y1={gainToY(lowGainDb, H)}
          y2={gainToY(lowGainDb, H)}
          className="multiband-graph__band-line"
        />
        <line
          x1={freqToX(lowMidFreq, W)}
          x2={freqToX(midHighFreq, W)}
          y1={gainToY(midGainDb, H)}
          y2={gainToY(midGainDb, H)}
          className="multiband-graph__band-line"
        />
        <line
          x1={freqToX(midHighFreq, W)}
          x2={W}
          y1={gainToY(highGainDb, H)}
          y2={gainToY(highGainDb, H)}
          className="multiband-graph__band-line"
        />

        {/* Band gain handles */}
        <circle
          cx={freqToX(bandCenters.low, W)}
          cy={gainToY(lowGainDb, H)}
          r="9"
          className="multiband-graph__band-handle"
          onPointerDown={handlePointerDown('lowGain')}
        />
        <circle
          cx={freqToX(bandCenters.mid, W)}
          cy={gainToY(midGainDb, H)}
          r="9"
          className="multiband-graph__band-handle"
          onPointerDown={handlePointerDown('midGain')}
        />
        <circle
          cx={freqToX(bandCenters.high, W)}
          cy={gainToY(highGainDb, H)}
          r="9"
          className="multiband-graph__band-handle"
          onPointerDown={handlePointerDown('highGain')}
        />

        {/* Labels */}
        <text x={freqToX(lowMidFreq, W) + 6} y="44" className="multiband-graph__label">
          {Math.round(lowMidFreq)} Hz
        </text>
        <text x={freqToX(midHighFreq, W) + 6} y="44" className="multiband-graph__label">
          {midHighFreq >= 1000 ? `${(midHighFreq / 1000).toFixed(1)} kHz` : `${Math.round(midHighFreq)} Hz`}
        </text>
      </svg>
    </div>
  )
}
